import { Tile, TileStripe } from "@dash/tile";
import { useTheme } from "@emotion/react";
import { appLinkTreesObject } from "@megarax/links";
import { formatDistanceToNow } from "date-fns";
import { pl } from "date-fns/locale";

import { isSignalGroupUnread, SignalDocumentGroup } from "./groupSignals";
import { SignalAppChips, SignalLevelChip } from "./SignalChips";
import { SignalAppIcon } from "./SignalAppIcon";
import { SignalLabelsList } from "./SignalLabelsList";
import { signalHref } from "./signalHref";
import { signalColor, topSignalLevel } from "./signalLevel";

type Props = {
  group: SignalDocumentGroup;
  onNavigate: (href: string, group: SignalDocumentGroup) => void;
};

export const SignalDocumentTile: React.FC<Props> = ({ group, onNavigate }) => {
  const theme = useTheme();
  const primarySignal = group.signals[0];
  const level = topSignalLevel(group.signals);
  const uniqueApps = [...new Set(group.signals.map((signal) => signal.app))];
  const isUnread = isSignalGroupUnread(group);
  const appColor = appLinkTreesObject[primarySignal.app]?.color ?? theme.colors.primary;
  const href = signalHref(primarySignal);

  const handleClick = () => {
    if (!href) return;
    onNavigate(href, group);
  };

  return (
    <Tile stripe={<TileStripe color={signalColor(level, theme)} />} onClick={href ? handleClick : undefined}>
      <div css={{ display: "flex", flexDirection: "column", gap: "0.375rem", fontSize: "0.875rem", minWidth: 0 }}>
        <div css={{ display: "flex", alignItems: "flex-start", gap: "0.625rem", minWidth: 0 }}>
          <SignalAppIcon app={primarySignal.app} isUnread={isUnread} />
          <div css={{ display: "flex", flex: 1, flexDirection: "column", gap: "0.125rem", minWidth: 0 }}>
            <div css={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.5rem" }}>
              <h3
                css={{
                  color: appColor,
                  margin: 0,
                  fontSize: "0.9375rem",
                  fontWeight: isUnread ? 700 : 500,
                  wordBreak: "break-word",
                }}
              >
                {group.name}
              </h3>
              <span
                css={{
                  color: theme.colors.secondaryText,
                  fontSize: "0.75rem",
                  whiteSpace: "nowrap",
                  fontWeight: isUnread ? 700 : 400,
                  flexShrink: 0,
                }}
              >
                {formatDistanceToNow(primarySignal.createdAt, { addSuffix: true, locale: pl })}
              </span>
            </div>
            <SignalLabelsList group={group} color={theme.colors.default} readOpacity={0.85} />
          </div>
        </div>
        <div css={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.25rem", paddingLeft: "2.375rem" }}>
          <SignalAppChips apps={uniqueApps} />
          <SignalLevelChip level={level} />
        </div>
      </div>
    </Tile>
  );
};
